import applicationConstants from '../../../../Core/Utility/ApplicationConstants';
import { DemeterUnmappedFinancialInstrumentModel, MurexInstrumentModel } from '../../../../Generated/Raven-Demeter';
import TextButton from '../../../Components/Form/Buttons/TextButton';
import OverflowCellWithTooltip from '../../../Components/Tables/OverflowCellWithTooltip/OverflowCellWithTooltip';
import { TableHeader } from '../../../Components/Tables/Table/TableDefinitions';
import formattingService from '../../../Services/Formatting/FormattingService';
import languageService from '../../../Services/Language/LanguageService';
import { translationKeys } from '../../../Services/Language/TranslationDefinitions';

export type MurexInstrumentRow = MurexInstrumentModel & { isSelected?: boolean };

// The grid, the filter bar and the edit dialog must label an exchange identically, so they all
// go through these. The translation maps do not cover every enum value (and are keyed in mixed
// case), so fall back to the label the backend supplied and finally to the raw code, which is
// already a readable exchange abbreviation.
const getLabelFromTranslationMap = (translationMap: { [key: string]: string }, value?: string | null, backendLabel?: string | null): string => {
    if (!value) {
        return backendLabel || applicationConstants.TablePlaceholderZeroOrEmpty;
    }

    const translationKey = translationMap[value];
    if (!translationKey) {
        return backendLabel || value;
    }

    return languageService.translate(translationKey);
};

export const getExchangeLabel = (exchange?: string | null, backendLabel?: string | null): string =>
    getLabelFromTranslationMap(translationKeys.exchange, exchange, backendLabel);

export const getSubExchangeLabel = (subExchange?: string | null, backendLabel?: string | null): string =>
    getLabelFromTranslationMap(translationKeys.subExchange, subExchange, backendLabel);

export const getCurrencyLabel = (currency?: string | null): string => getLabelFromTranslationMap(translationKeys.currency, currency);

// Murex names every market after its sector, e.g. 'Agriculture - Dairy'. Every instrument on this
// screen shares the sector, so it only costs column width; the grid and the filter both drop it.
// The unshortened value stays the filter's value because that is what the endpoint matches on.
const murexMarketSectorPrefix = 'Agriculture - ';

export const getMurexMarketLabel = (murexMarket?: string | null): string => {
    if (!murexMarket) {
        return applicationConstants.TablePlaceholderZeroOrEmpty;
    }

    return murexMarket.startsWith(murexMarketSectorPrefix) ? murexMarket.slice(murexMarketSectorPrefix.length) : murexMarket;
};

export const murexInstrumentColumnDefinitions: TableHeader<MurexInstrumentRow>[] = [
    {
        // Every other column is pinned to the width its content needs, and they total 1180px with
        // the checkbox. This one carries no width on purpose: the shared Table renders columns as
        // flex items that cannot grow, so the one column left without a width keeps its full-width
        // basis and shrinks to exactly the space the pinned ones leave. That makes the grid finish
        // flush with the page and hands the slack to the longest value. The pinned widths and the
        // minimum below add up to 1400px, so that is the narrowest page the grid still fits.
        field: 'murexDerivedInstrumentKey',
        headerValueGetter: () => languageService.translate(translationKeys.murexInstrumentMapping.fields.instrumentKey),
        minimumWidth: 220,
        pinned: 'left',
        cellRenderer: (parameters) => <OverflowCellWithTooltip value={parameters.murexDerivedInstrumentKey} />,
    },
    {
        field: 'murexMarket',
        headerValueGetter: () => languageService.translate(translationKeys.murexInstrumentMapping.fields.murexMarket),
        // Narrower than the other text columns because dropping the sector prefix leaves short names.
        width: 130,
        minimumWidth: 130,
        cellRenderer: (parameters) => getMurexMarketLabel(parameters.murexMarket),
    },
    {
        field: 'murexProduct',
        headerValueGetter: () => languageService.translate(translationKeys.murexInstrumentMapping.fields.murexProduct),
        width: 120,
        minimumWidth: 120,
    },
    {
        field: 'exchange',
        headerValueGetter: () => languageService.translate(translationKeys.words.exchange),
        width: 100,
        minimumWidth: 100,
        // Only mapped rows carry an exchange; unmapped ones arrive null and fall through to the placeholder.
        cellRenderer: (parameters) => getExchangeLabel(parameters.exchange),
    },
    {
        field: 'subExchange',
        headerValueGetter: () => languageService.translate(translationKeys.murexInstrumentMapping.fields.subExchange),
        width: 110,
        minimumWidth: 110,
        cellRenderer: (parameters) => getSubExchangeLabel(parameters.subExchange),
    },
    {
        field: 'commodityDisplayName',
        headerValueGetter: () => languageService.translate(translationKeys.words.commodity),
        width: 150,
        minimumWidth: 150,
        cellRenderer: (parameters) => <OverflowCellWithTooltip value={parameters.commodityDisplayName ?? applicationConstants.TablePlaceholderZeroOrEmpty} />,
    },
    {
        field: 'currency',
        headerValueGetter: () => languageService.translate(translationKeys.words.currency),
        width: 90,
        minimumWidth: 90,
        cellRenderer: (parameters) => getCurrencyLabel(parameters.currency),
    },
    {
        field: 'contractSize',
        headerValueGetter: () => languageService.translate(translationKeys.murexInstrumentMapping.fields.contractSize),
        width: 110,
        minimumWidth: 110,
        cellRenderer: (parameters) => formattingService.formatNumber(parameters.contractSize, { zeroPlaceholder: true }),
    },
    {
        field: 'modifiedAt',
        headerValueGetter: () => languageService.translate(translationKeys.text.lastUpdated),
        // Fits 'Sep 4, 2026 10:23 AM', which has no tooltip to fall back on.
        width: 160,
        minimumWidth: 160,
        cellRenderer: (parameters) => formattingService.toTimestampFromUtc(parameters.modifiedAt) || applicationConstants.TablePlaceholderZeroOrEmpty,
    },
    {
        field: 'modifiedBy',
        headerValueGetter: () => languageService.translate(translationKeys.text.updatedBy),
        width: 150,
        minimumWidth: 150,
        cellRenderer: (parameters) => <OverflowCellWithTooltip value={parameters.modifiedBy ?? applicationConstants.TablePlaceholderZeroOrEmpty} />,
    },
];

export const getUnmappedFinancialInstrumentColumnDefinitions = (
    handleMurexInstrumentKeySelected: (murexDerivedInstrumentKey: string) => void,
): TableHeader<DemeterUnmappedFinancialInstrumentModel>[] => [
    {
        field: 'stoneXInstrumentId',
        headerValueGetter: () => languageService.translate(translationKeys.murexInstrumentMapping.fields.stoneXInstrumentId),
        width: 200,
        minimumWidth: 200,
        cellRenderer: (parameters) => <OverflowCellWithTooltip value={parameters.stoneXInstrumentId ?? applicationConstants.TablePlaceholderZeroOrEmpty} />,
    },
    {
        field: 'sourceSystem',
        headerValueGetter: () => languageService.translate(translationKeys.murexInstrumentMapping.fields.sourceSystem),
        width: 120,
        minimumWidth: 120,
        cellRenderer: (parameters) => parameters.sourceSystem ?? applicationConstants.TablePlaceholderZeroOrEmpty,
    },
    {
        field: 'reason',
        headerValueGetter: () => languageService.translate(translationKeys.murexInstrumentMapping.fields.reason),
        width: 260,
        minimumWidth: 260,
        cellRenderer: (parameters) => <OverflowCellWithTooltip value={parameters.reason ?? applicationConstants.TablePlaceholderZeroOrEmpty} />,
    },
    {
        field: 'murexDerivedInstrumentKey',
        headerValueGetter: () => languageService.translate(translationKeys.murexInstrumentMapping.fields.murexKeyOrRicPrefix),
        minimumWidth: 220,
        cellRenderer: (parameters) => {
            const { murexDerivedInstrumentKey } = parameters;

            if (!murexDerivedInstrumentKey) {
                return parameters.reutersInstrumentCodePrefix ?? applicationConstants.TablePlaceholderZeroOrEmpty;
            }

            return (
                <TextButton
                    title={murexDerivedInstrumentKey}
                    onClick={() => handleMurexInstrumentKeySelected(murexDerivedInstrumentKey)}
                    track={{ eventCategory: 'MurexInstrumentMapping', eventAction: 'UnmappedInstrumentKeyClicked' }}
                />
            );
        },
    },
];
